/**
 * Static course geometry in typed arrays, plus the queries the moves use.
 * Built once from CourseData; never mutated by step().
 */

import { type CourseData, SolidClass } from "./types";

export class World {
  readonly name: string;
  /** Display name (the course's `title`, or its `name`). */
  readonly title: string;
  readonly startX: number;
  readonly startY: number;
  /** True for a timed course (version 2 with a start and a finish line). */
  readonly timed: boolean;
  /** Start and finish line x on a timed course; NaN otherwise. */
  readonly startLineX: number;
  readonly finishLineX: number;

  readonly solidCount: number;
  readonly solidX0: Float64Array;
  readonly solidY0: Float64Array;
  readonly solidX1: Float64Array;
  readonly solidY1: Float64Array;
  /** SolidClass of each solid (a plain solid, a box, a pipe). */
  readonly solidClass: Uint8Array;

  readonly waterCount: number;
  readonly waterX0: Float64Array;
  readonly waterY0: Float64Array;
  readonly waterX1: Float64Array;
  readonly waterY1: Float64Array;

  readonly barCount: number;
  readonly barX: Float64Array;
  readonly barY: Float64Array;
  /** The ladder a bar is a rung of (-1 for a plain bar) and which rung, from 0 at the bottom. */
  readonly barLadder: Int32Array;
  readonly barRung: Int32Array;

  /** Salmon ladders: the pegs' x, the lowest rung's y, the rung count and spacing, the first rung bar's index. */
  readonly ladderCount: number;
  readonly ladderX: Float64Array;
  readonly ladderY0: Float64Array;
  readonly ladderRungs: Int32Array;
  readonly ladderSpacing: Float64Array;
  readonly ladderFirstBar: Int32Array;

  readonly respawnCount: number;
  readonly respawnX: Float64Array;
  readonly respawnY: Float64Array;
  /** Section names of the respawn points ("" when unnamed). */
  readonly respawnNames: readonly string[];

  readonly markCount: number;
  readonly markX: Float64Array;
  readonly markNames: readonly string[];

  /** Extent of everything in the course, for the camera and the editor. */
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;

  /** Scratch result of the last nearestBar() query. */
  queryBar = -1;
  queryDist = 0;

  constructor(course: CourseData) {
    validateCourse(course);
    this.name = course.name;
    this.title = typeof course.title === "string" && course.title.length > 0 ? course.title : course.name;
    this.startX = course.start.x;
    this.startY = course.start.y;
    this.timed = typeof course.startLine === "number" && typeof course.finishLine === "number";
    this.startLineX = this.timed ? (course.startLine as number) : Number.NaN;
    this.finishLineX = this.timed ? (course.finishLine as number) : Number.NaN;

    // Boxes and pipes collide as solids; their class says what else they are. Course order is kept.
    const solids = course.entities.filter((e) => e.type === "solid" || e.type === "box" || e.type === "pipe");
    const waters = course.entities.filter((e) => e.type === "water");
    const bars = course.entities.filter((e) => e.type === "bar");
    const ladders = course.entities.filter((e) => e.type === "ladder");

    this.solidCount = solids.length;
    this.solidX0 = new Float64Array(solids.length);
    this.solidY0 = new Float64Array(solids.length);
    this.solidX1 = new Float64Array(solids.length);
    this.solidY1 = new Float64Array(solids.length);
    this.solidClass = new Uint8Array(solids.length);
    solids.forEach((s, i) => {
      this.solidClass[i] =
        s.type === "box" ? SolidClass.Box : s.type === "pipe" ? SolidClass.Pipe : SolidClass.Solid;
      this.solidX0[i] = s.x;
      this.solidY0[i] = s.y;
      this.solidX1[i] = s.x + s.w;
      this.solidY1[i] = s.y + s.h;
    });

    this.waterCount = waters.length;
    this.waterX0 = new Float64Array(waters.length);
    this.waterY0 = new Float64Array(waters.length);
    this.waterX1 = new Float64Array(waters.length);
    this.waterY1 = new Float64Array(waters.length);
    waters.forEach((w, i) => {
      this.waterX0[i] = w.x;
      this.waterY0[i] = w.y;
      this.waterX1[i] = w.x + w.w;
      this.waterY1[i] = w.y + w.h;
    });

    // Every rung of a ladder is a bar (after the plain bars, so a ladder added to a course keeps the
    // other bars' ids); the grip rule in barGrippable() lets only the rung the ladder's bar sits on be taken.
    let rungBars = 0;
    for (const l of ladders) rungBars += l.rungs;
    this.barCount = bars.length + rungBars;
    this.barX = new Float64Array(this.barCount);
    this.barY = new Float64Array(this.barCount);
    this.barLadder = new Int32Array(this.barCount).fill(-1);
    this.barRung = new Int32Array(this.barCount);
    bars.forEach((b, i) => {
      this.barX[i] = b.x;
      this.barY[i] = b.y;
    });
    this.ladderCount = ladders.length;
    this.ladderX = new Float64Array(ladders.length);
    this.ladderY0 = new Float64Array(ladders.length);
    this.ladderRungs = new Int32Array(ladders.length);
    this.ladderSpacing = new Float64Array(ladders.length);
    this.ladderFirstBar = new Int32Array(ladders.length);
    let next = bars.length;
    ladders.forEach((l, i) => {
      this.ladderX[i] = l.x;
      this.ladderY0[i] = l.y;
      this.ladderRungs[i] = l.rungs;
      this.ladderSpacing[i] = l.spacing;
      this.ladderFirstBar[i] = next;
      for (let k = 0; k < l.rungs; k++) {
        this.barX[next] = l.x;
        this.barY[next] = l.y + k * l.spacing;
        this.barLadder[next] = i;
        this.barRung[next] = k;
        next++;
      }
    });

    const respawns = course.respawns.length > 0 ? course.respawns : [course.start];
    this.respawnCount = respawns.length;
    this.respawnX = new Float64Array(respawns.length);
    this.respawnY = new Float64Array(respawns.length);
    respawns.forEach((r, i) => {
      this.respawnX[i] = r.x;
      this.respawnY[i] = r.y;
    });
    this.respawnNames = respawns.map((r) => ("name" in r && typeof r.name === "string" ? r.name : ""));

    this.markCount = course.marks.length;
    this.markX = new Float64Array(course.marks.length);
    this.markNames = course.marks.map((m) => m.name);
    course.marks.forEach((m, i) => {
      this.markX[i] = m.x;
    });

    let minX = course.start.x;
    let maxX = course.start.x;
    let minY = course.start.y;
    let maxY = course.start.y;
    for (const e of course.entities) {
      minX = Math.min(minX, e.x);
      minY = Math.min(minY, e.y);
      if (e.type === "bar") {
        maxX = Math.max(maxX, e.x);
        maxY = Math.max(maxY, e.y);
      } else if (e.type === "ladder") {
        maxX = Math.max(maxX, e.x);
        maxY = Math.max(maxY, e.y + (e.rungs - 1) * e.spacing);
      } else {
        maxX = Math.max(maxX, e.x + e.w);
        maxY = Math.max(maxY, e.y + e.h);
      }
    }
    this.minX = minX;
    this.maxX = maxX;
    this.minY = minY;
    this.maxY = maxY;
  }

  /** Index of the first solid overlapping the open box, or -1. */
  overlapSolid(x0: number, y0: number, x1: number, y1: number, skip = -1): number {
    const sx0 = this.solidX0;
    const sy0 = this.solidY0;
    const sx1 = this.solidX1;
    const sy1 = this.solidY1;
    for (let i = 0; i < this.solidCount; i++) {
      if (i === skip) continue;
      if (x0 < sx1[i]! && x1 > sx0[i]! && y0 < sy1[i]! && y1 > sy0[i]!) return i;
    }
    return -1;
  }

  /**
   * The nearest solid of class `cls` ahead of a body `radius` wide with its feet at (x, feetY), moving
   * in `dir`, whose near face is within `range`: for a box, one standing on the feet's level (its
   * bottom at or below the feet and its top above them); for a pipe, one the standing body would hit
   * (its bottom above the feet and below `height` over them). The gap to its near face is in
   * `queryDist`. Returns the solid index or -1.
   */
  nextObstacleAhead(
    x: number,
    feetY: number,
    radius: number,
    height: number,
    dir: number,
    cls: number,
    range: number,
  ): number {
    let best = -1;
    let bestD = range;
    for (let i = 0; i < this.solidCount; i++) {
      if (this.solidClass[i] !== cls) continue;
      const y0 = this.solidY0[i]!;
      const y1 = this.solidY1[i]!;
      if (cls === SolidClass.Box) {
        if (y0 > feetY + 1e-6 || y1 <= feetY + 1e-6) continue;
      } else if (y0 <= feetY + 1e-6 || y0 >= feetY + height) continue;
      const d = dir > 0 ? this.solidX0[i]! - (x + radius) : x - radius - this.solidX1[i]!;
      if (d < -1e-6 || d > bestD) continue;
      bestD = d < 0 ? 0 : d;
      best = i;
    }
    this.queryDist = best >= 0 ? bestD : 0;
    return best;
  }

  /** True if the open box overlaps any water. */
  overlapWater(x0: number, y0: number, x1: number, y1: number): boolean {
    for (let i = 0; i < this.waterCount; i++) {
      if (x0 < this.waterX1[i]! && x1 > this.waterX0[i]! && y0 < this.waterY1[i]! && y1 > this.waterY0[i]!) {
        return true;
      }
    }
    return false;
  }

  /**
   * Highest solid top within `depth` below `feetY` under the box [x0, x1], or
   * -Infinity when nothing is there. Touching counts.
   */
  probeDown(x0: number, x1: number, feetY: number, depth: number, skip = -1): number {
    let best = Number.NEGATIVE_INFINITY;
    const lo = feetY - depth;
    for (let i = 0; i < this.solidCount; i++) {
      if (i === skip) continue;
      if (x0 < this.solidX1[i]! && x1 > this.solidX0[i]!) {
        const top = this.solidY1[i]!;
        if (top <= feetY + 1e-9 && top >= lo && top > best && this.solidY0[i]! < feetY) best = top;
      }
    }
    return best;
  }

  /**
   * Vertical room above `feetY` under the box [x0, x1]: the lowest solid
   * bottom that is at or above `feetY`, minus `feetY`. Infinity when open.
   */
  roomAbove(x0: number, x1: number, feetY: number, skip = -1): number {
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.solidCount; i++) {
      if (i === skip) continue;
      if (x0 < this.solidX1[i]! && x1 > this.solidX0[i]!) {
        const bottom = this.solidY0[i]!;
        if (bottom >= feetY - 1e-9 && bottom - feetY < best) best = bottom - feetY;
      }
    }
    return best;
  }

  /**
   * Nearest bar to (px, py) within `radius`, skipping `exclude`. Result in
   * queryBar / queryDist. Returns the bar index or -1.
   */
  /**
   * Whether bar `i` can be gripped: a plain bar always, a ladder's rung only where the ladder's bar
   * sits, which is `activeRung` on `activeLadder` (the one the player is on) and the bottom rung on
   * every other ladder.
   */
  barGrippable(i: number, activeLadder: number, activeRung: number): boolean {
    const ladder = this.barLadder[i]!;
    if (ladder < 0) return true;
    return this.barRung[i] === (ladder === activeLadder ? activeRung : 0);
  }

  nearestBar(
    px: number,
    py: number,
    radius: number,
    exclude: number,
    activeLadder = -1,
    activeRung = 0,
  ): number {
    let best = -1;
    let bestD2 = radius * radius;
    for (let i = 0; i < this.barCount; i++) {
      if (i === exclude || !this.barGrippable(i, activeLadder, activeRung)) continue;
      const dx = this.barX[i]! - px;
      const dy = this.barY[i]! - py;
      const d2 = dx * dx + dy * dy;
      if (d2 <= bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    this.queryBar = best;
    this.queryDist = best >= 0 ? Math.sqrt(bestD2) : 0;
    return best;
  }
}

export class CourseError extends Error {}

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** Throws CourseError with a readable message when the data is malformed. */
export function validateCourse(course: unknown): asserts course is CourseData {
  if (typeof course !== "object" || course === null) throw new CourseError("course must be an object");
  const c = course as Record<string, unknown>;
  if (c.version !== 0 && c.version !== 1 && c.version !== 2)
    throw new CourseError(`unsupported course version ${String(c.version)}`);
  if (typeof c.name !== "string" || c.name.length === 0)
    throw new CourseError("course.name must be a string");
  const start = c.start as Record<string, unknown> | undefined;
  if (!start || !isNum(start.x) || !isNum(start.y)) throw new CourseError("course.start must be {x, y}");
  if (c.title !== undefined && typeof c.title !== "string")
    throw new CourseError("course.title must be a string");
  if (c.startLine !== undefined || c.finishLine !== undefined) {
    if (c.version !== 2) throw new CourseError("startLine and finishLine need course version 2");
    if (!isNum(c.startLine) || !isNum(c.finishLine))
      throw new CourseError("a timed course needs numeric startLine and finishLine");
    if (!(start.x < c.startLine && c.startLine < c.finishLine))
      throw new CourseError("a timed course needs start.x < startLine < finishLine");
  }
  if (!Array.isArray(c.respawns)) throw new CourseError("course.respawns must be an array");
  c.respawns.forEach((r: Record<string, unknown>, i) => {
    if (!r || !isNum(r.x) || !isNum(r.y)) throw new CourseError(`respawns[${i}] must be {x, y}`);
  });
  if (!Array.isArray(c.marks)) throw new CourseError("course.marks must be an array");
  c.marks.forEach((m: Record<string, unknown>, i) => {
    if (!m || typeof m.name !== "string" || !isNum(m.x))
      throw new CourseError(`marks[${i}] must be {name, x}`);
  });
  if (!Array.isArray(c.entities)) throw new CourseError("course.entities must be an array");
  c.entities.forEach((e: Record<string, unknown>, i) => {
    if (!e || typeof e.type !== "string") throw new CourseError(`entities[${i}] must have a type`);
    if (!isNum(e.x) || !isNum(e.y)) throw new CourseError(`entities[${i}] must have numeric x, y`);
    if ((e.type === "box" || e.type === "pipe") && c.version !== 1 && c.version !== 2) {
      throw new CourseError(`entities[${i}] (${e.type}) needs course version 1`);
    }
    switch (e.type) {
      case "solid":
      case "box":
      case "pipe":
      case "water":
        if (!isNum(e.w) || !isNum(e.h) || e.w <= 0 || e.h <= 0) {
          throw new CourseError(`entities[${i}] (${e.type}) must have positive w, h`);
        }
        break;
      case "bar":
        break;
      case "ladder":
        if (c.version === 0) throw new CourseError(`entities[${i}] (ladder) needs course version 1`);
        if (!isNum(e.rungs) || !Number.isInteger(e.rungs) || e.rungs < 2)
          throw new CourseError(`entities[${i}] (ladder) needs an integer rungs of at least 2`);
        if (!isNum(e.spacing) || e.spacing <= 0)
          throw new CourseError(`entities[${i}] (ladder) needs a positive spacing`);
        break;
      default:
        throw new CourseError(`entities[${i}] has unknown type "${e.type}"`);
    }
  });
}
